import type { Path } from '@cobliteam/api-lib/src/__generated__/graphql';
import { useGetImages } from '@cobliteam/hooks/src/useCamera/useFaceId';
import useVehiclePastAssociations from '@cobliteam/hooks/src/useEntity/useVehiclePastAssociations';
import { useTranslation } from '@cobliteam/i18n';
import Divider from '@cobliteam/ui/components/Divider';
import Drawer, { DrawerBody, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from '@cobliteam/ui/components/Drawer';
import Popover, { PopoverContent, PopoverIcon, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import Typography from '@cobliteam/ui/components/Typography';
import type { IconNames } from '@cobliteam/ui/icons';
import Icon from '@cobliteam/ui/icons';
import { formatISO } from '@cobliteam/utils/date';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState } from 'react';
import { AssociationSource, sourceFromIdentificationMethod, useAssociationSourceLabels } from '../../MultiSelects/MultiSelectAssociationSource/MultiSelectAssociationSource';
import AssociationHistoryItem from './components/AssociationHistoryItem';
import DrawerLoadingSkeleton from './components/DrawerLoadingSkeleton';

interface AssociationHistoryDrawerProps {
  path: Path;
}

const AssociationHistoryDrawer = ({ path }: AssociationHistoryDrawerProps) => {
  const { t } = useTranslation('path-association');
  const vehicleId = path.vehicle?.id;
  const associationLabels = useAssociationSourceLabels();

  const [open, setOpen] = useState(false);

  const { data, isFetching } = useVehiclePastAssociations(
    {
      vehicleId: vehicleId!,
      referenceTimestamp: formatISO(path.startTime + 1000),
    },
    { enabled: open && !!vehicleId }
  );

  const { data: images = { content: [] }, isLoading: isImagesLoading } = useGetImages(vehicleId || '', {
    fromTimestamp: new Date(path.startTime).toISOString(),
    toTimestamp: new Date(path.endTime).toISOString(),
    page: 0,
    size: 100,
  });

  const associationsToDisplay = useMemo(() => {
    if (!data) {
      return [];
    }

    return data
      .map((association, idx) => {
        const nextAssociation = idx < data.length - 1 ? data[idx + 1] : undefined;

        return {
          ...association,
          previousDriver: nextAssociation?.driver,
        };
      })
      .filter((association) => association.previousDriver || association.driver);
  }, [data]);

  const faceIdReferenceAssociation = useMemo(() => {
    if (!data?.length) {
      return undefined;
    }

    return data.find((association) => Boolean(association.associatedAt));
  }, [data]);

  const hasAssociations = associationsToDisplay.length > 0;
  const isLoading = isFetching && !data;
  const showEmptyState = !(isLoading || hasAssociations);
  const sourceAsEnum = path.associationSource ? sourceFromIdentificationMethod(path.associationSource) : undefined;

  const getAssociationSourceLabel = (): { icon: IconNames; label: string; showTooltip?: boolean; isFaceId?: boolean } | undefined => {
    if (!path.driver) {
      return {
        icon: 'history',
        label: t('view_history'),
      };
    }

    const commonPrefix = 'Associado por';

    switch (sourceAsEnum) {
      case AssociationSource.FACE_ID:
        return {
          icon: 'scan-face',
          label: t('face_id_label'),
          showTooltip: images?.content.length === 0,
          isFaceId: true,
        };
      case AssociationSource.MANUAL:
        return {
          icon: 'user-pen',
          label: 'Associado manualmente por usuário',
        };
      case AssociationSource.APP:
        return {
          icon: 'smartphone',
          label: `${commonPrefix} ${associationLabels[sourceAsEnum].toLowerCase()}`,
        };
      case AssociationSource.IDENTIFIER:
        return {
          icon: 'credit-card',
          label: `${commonPrefix} ${associationLabels[sourceAsEnum].toLowerCase()}`,
        };
      case AssociationSource.DASHBOARD:
        return {
          icon: 'user',
          label: `${commonPrefix} ${associationLabels[sourceAsEnum].toLowerCase()}`,
        };

      default:
        return undefined;
    }
  };

  const associationSourceData = getAssociationSourceLabel();

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <div className='flex items-center gap-1'>
        <DrawerTrigger asChild>
          <div className='flex cursor-pointer items-start gap-1'>
            <Icon name={associationSourceData?.icon || 'history'} className='shrink-0' />
            <Typography variant='caption-default' className='break-before-auto text-gray-11 underline'>
              {associationSourceData?.label || t('view_history')}
            </Typography>
          </div>
        </DrawerTrigger>
        {associationSourceData?.showTooltip && (
          <Popover>
            <PopoverTrigger>
              <PopoverIcon icon={<Icon name='circle-help' className='shrink-0 text-gray-11' />} />
            </PopoverTrigger>
            <PopoverContent className='max-w-[320px] space-y-1'>
              <Typography variant='text-md-semibold'>{t('face_id_tooltip_title')}</Typography>
              <Typography variant='text-md'>{t('face_id_tooltip_body')}</Typography>
            </PopoverContent>
          </Popover>
        )}
      </div>
      <DrawerContent size='sm'>
        <DrawerHeader>
          <DrawerTitle>{t('association_history.title')}</DrawerTitle>
          <DrawerClose />
        </DrawerHeader>
        <DrawerBody className='flex-col gap-4 overflow-y-auto'>
          <AnimatePresence mode='wait'>
            {isLoading && (
              <motion.div key='loading' initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <DrawerLoadingSkeleton />
              </motion.div>
            )}
            {showEmptyState && (
              <motion.div
                key='empty'
                className='flex items-center justify-center py-8'
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <Typography color='secondary'>{t('association_history.empty_state')}</Typography>
              </motion.div>
            )}
            {!isLoading && hasAssociations && (
              <motion.div key='list' initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                <motion.div
                  initial='hidden'
                  animate='visible'
                  variants={{
                    visible: {
                      transition: {
                        staggerChildren: 0.1,
                      },
                    },
                  }}
                  className='flex flex-col gap-4'
                >
                  {associationsToDisplay.map((association, idx) => (
                    <motion.div
                      key={`${association.associatedAt}-${idx}`}
                      className='flex flex-col gap-4'
                      variants={{
                        hidden: { opacity: 0, y: 20 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.3,
                            ease: 'easeOut',
                          },
                        },
                      }}
                    >
                      <AssociationHistoryItem association={association} />
                      {idx < associationsToDisplay.length - 1 && <Divider />}
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default AssociationHistoryDrawer;
